import { agTableRef, YesOrNo } from '@constants';
import { IAgApiEditorParams } from '@containers/AgTable/ApiEditor';
import { IAgInputEditorParams } from '@containers/AgTable/InputEditor';
import { IAgNumberEditorParams } from '@containers/AgTable/NumberEditor';
import { Prototype } from '@core';
import loadable from '@loadable/component';
import { Checkbox } from '@material-ui/core';
import { CellPosition, ColDef, ICellRendererParams } from 'ag-grid-community';
import { groupBy, isNumber } from 'lodash';
import { CSSProperties } from 'react';
import {
  KChip,
  KChipProps,
  KColors,
  KContainer,
  KLabel,
  KTextProps
} from 'uikit';

const ApiEditor = loadable(() => import('@containers/AgTable/ApiEditor'));
const InputEditor = loadable(() => import('@containers/AgTable/InputEditor'));
const NumberEditor = loadable(() => import('@containers/AgTable/NumberEditor'));

interface ITableChip extends Omit<KChipProps, 'label'> {
  label?: string;
}

interface IEditingRow {
  rowIndex: string | number;
  columns: CellPosition[];
}

export default class AgUtils {
  static options = {
    centerContent: {
      cellClass: ['ag-cell-full-width', 'ag-cell-content-center']
    },

    baseXs: { minWidth: 60 },
    baseSm: { minWidth: 80 },
    baseNm: { minWidth: 100 },
    baseMd: { minWidth: 120 },
    baseXMd: { minWidth: 130 },
    baseLg: { minWidth: 140 },
    baseXLg: { minWidth: 160 },
    base2XLg: { minWidth: 180 },
    base3XLg: { minWidth: 200 },
    base4XLg: { minWidth: 220 },
    base5XLg: { minWidth: 240 },

    text: (css?: KTextProps) => ({
      cellRenderer: ({ value: v }: ICellRendererParams<any>) => {
        if (v) {
          return <KLabel.Text {...css}>{v}</KLabel.Text>;
        }

        return null;
      }
    }),

    chip: (data?: {
      css?: CSSProperties;
      uppercase?: boolean;
      width?: number;
      props?: ITableChip;
    }) => ({
      cellRenderer: ({ value: v }: ICellRendererParams<any>) => {
        if (v) {
          return (
            <KChip
              background={v?.backgroundColor || '#DEFFFD'}
              brC={v?.border || KColors.primary.normal}
              label={v?.name ?? v}
              textTransform={data?.uppercase ? 'uppercase' : 'capitalize'}
              color={v?.color || KColors.primary.normal}
              width={data?.width}
              {...data?.props}
            />
          );
        }

        return null;
      }
    }),
    chips: (data: {
      uppercase?: boolean;
      width?: number;
      mapLabelData: {
        [key: string]: ITableChip;
      };
      notFoundKey?: string;
    }) => ({
      cellRenderer: ({ value: v }: ICellRendererParams<any>) => {
        const { uppercase = true, notFoundKey, mapLabelData } = data;
        let item = v;
        if (v) {
          if (typeof v === 'boolean') {
            item = {
              name: v,
              ...(mapLabelData?.[`${v}`] ??
                mapLabelData?.[`${v}`.toLowerCase()])
            };
          } else {
            item = {
              name: v,
              ...(mapLabelData?.[v] ?? mapLabelData?.[v.toLowerCase()])
            };
          }
        } else if (notFoundKey) {
          item = {
            name: notFoundKey,
            ...(mapLabelData?.[`${notFoundKey}`] ??
              mapLabelData?.[`${notFoundKey}`.toLowerCase()])
          };
        }

        if (item) {
          const { name, label, ...rest } = item;
          return (
            <KChip
              background={item?.backgroundColor || '#DEFFFD'}
              brC={KColors.primary.normal}
              label={label ?? name ?? ''}
              textTransform={uppercase ? 'uppercase' : 'capitalize'}
              color={KColors.primary.normal}
              width={data?.width}
              {...rest}
            />
          );
        }

        return null;
      }
    }),

    currency: (defaultValue: any = '', options?: any) => ({
      cellRenderer: ({ value: v }: ICellRendererParams<any>) => {
        return !Prototype.core.isNullOrUndefined(v) && isNumber(v)
          ? Prototype.number.formatCurrency(v, {
              withAbs: v < 0,
              withSpace: true,
              ...options
            })
          : !Prototype.core.isNullOrUndefined(v) &&
              isNumber(parseFloat(v as string))
            ? Prototype.number.formatCurrency(v, {
                withAbs: parseFloat(v as string) < 0,
                withSpace: true,
                ...options
              })
            : defaultValue;
      }
    }),

    boolean: (data: [any, any] = [YesOrNo.Yes, YesOrNo.No]) => ({
      cellRenderer: ({ value: v }: ICellRendererParams<any>) => {
        return v ? data[0] : data[1];
      }
    }),

    checkbox: {
      cellClass: ['ag-cell-full-width', 'ag-cell-content-center'],
      cellRenderer: ({ value: v }: ICellRendererParams<any>) => {
        return (
          <KContainer.View dp="flex" justifyContent="center">
            <Checkbox checked={v} color="primary" />
          </KContainer.View>
        );
      }
    },

    withEllipsis: {
      cellRenderer: ({ value: v }: ICellRendererParams<any>) =>
        v ? <KLabel.Text numberOfLines={4}>{v}</KLabel.Text> : null
    },
    withNA: {
      cellRenderer: ({ value: v }: ICellRendererParams<any>) => {
        return v || 'N/A';
      }
    }
  };

  static column = {
    refreshActionCol: (agTableName: string, colId?: string) => {
      const api = agTableRef[agTableName]?.gridRef?.current?.api;
      if (api) {
        api.refreshCells({
          force: true,
          columns: [colId ?? 'id']
        });
      }
    },
    refreshCols: (agTableName: string, colIds: string[] = []) => {
      const api = agTableRef[agTableName]?.gridRef?.current?.api;
      if (api) {
        api.refreshCells({
          force: true,
          columns: colIds
        });
      }
    }
  };

  static row = {
    getEditingRows: (agTableName: string): IEditingRow[] => {
      const api = agTableRef[agTableName]?.gridRef?.current?.api;
      const editingCells = api?.getEditingCells();

      if (!editingCells) {
        return [];
      }

      const grouped = groupBy(editingCells, 'rowIndex');
      return Object.entries(grouped).map(([rowIndex, columns]) => ({
        rowIndex: Number(rowIndex),
        columns
      }));
    }
  };

  static editCell = {
    text: (params?: IAgInputEditorParams): ColDef<any> => {
      return {
        editable: true,
        cellEditor: InputEditor,
        cellEditorParams: {
          ...params
        }
      };
    },
    number: (params?: IAgNumberEditorParams): ColDef<any> => {
      return {
        editable: true,
        cellEditor: NumberEditor,
        cellEditorParams: {
          ...params
        }
      };
    },
    api: (params: IAgApiEditorParams): ColDef<any> => {
      return {
        editable: true,
        cellEditor: ApiEditor,
        cellEditorParams: {
          ...params
        }
      };
    }
  };
}
